<template>
  <div ref="scrollEl" class="overflow-y-auto overflow-x-hidden bg-gray-100 p-4 space-y-3 font-proxima">
    <div v-if="messages.length === 0" class="text-sm text-gray-500 text-center pt-8 px-4">
      <p class="font-semibold text-gray-600 mb-1">Hi, I'm SAMMI.</p>
      <p>I can help you look up applicants, assistance requests, appointments, funds, and more — just ask.</p>
    </div>

    <template v-for="message in messages" :key="message.id">
      <template v-for="(part, index) in message.parts" :key="`${message.id}-${index}`">
        <div
          v-if="part.type === 'text' && part.text"
          class="flex"
          :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            v-if="message.role === 'user'"
            class="px-4 py-2 rounded-lg max-w-[85%] whitespace-pre-wrap text-sm bg-blue-600 text-white rounded-br-none"
          >{{ part.text }}</div>
          <div
            v-else
            class="agent-markdown px-4 py-2 rounded-lg text-sm bg-gray-300 text-gray-600 rounded-bl-none"
            :class="containsTable(part.text) ? 'max-w-full min-w-0' : 'max-w-[85%]'"
            @click="onMarkdownClick"
            v-html="renderMarkdown(part.text)"
          />
        </div>
        <div v-else-if="isToolPart(part) && richComponent(part)" class="space-y-1">
          <component :is="richComponent(part)" :part="part" />
        </div>
        <!-- Generic tool-call cards ("Done: <tool>" + JSON) are debug-only; the
             rich result components above always render. Typing dots still cover
             in-progress feedback when these are hidden. -->
        <AgentToolCallCard v-else-if="agentDebug && isToolPart(part)" :part="part" />
      </template>
    </template>

    <AgentConfirmActionCard v-if="agentStore.pendingConfirmation" />

    <div v-if="showTyping" class="flex justify-start">
      <div class="flex items-center gap-1.5 px-4 py-3 bg-gray-300 rounded-lg rounded-bl-none w-fit" aria-label="SAMMI is responding">
        <span class="typing-dot" />
        <span class="typing-dot" style="animation-delay: 0.15s" />
        <span class="typing-dot" style="animation-delay: 0.3s" />
      </div>
    </div>
    <div v-if="error" class="text-xs text-red-500">
      Something went wrong. Try again or start a new conversation.
    </div>
  </div>
</template>
<script setup lang="ts">
import { isToolUIPart } from 'ai';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { Component } from 'vue';
import ResultApplicants from './ResultApplicants.vue';
import ResultCases from './ResultCases.vue';
import ResultFundSnapshot from './ResultFundSnapshot.vue';
import ResultFundingSources from './ResultFundingSources.vue';
import ResultFunds from './ResultFunds.vue';
import ResultStaff from './ResultStaff.vue';
import ResultLedger from './ResultLedger.vue';
import ResultAppointments from './ResultAppointments.vue';
import ResultFundingSourceSnapshot from './ResultFundingSourceSnapshot.vue';

const agentStore = useAgentStore();

// Debug flag (VITE_AGENT_DEBUG / NUXT_PUBLIC_AGENT_DEBUG): gates the generic
// tool-call cards. Off by default for a clean chat.
const agentDebug = useRuntimeConfig().public.agentDebug;

// Generative UI registry: tool results with a purpose-built renderer. Anything
// unmapped (or still streaming, or errored) falls back to the generic ToolCallCard.
const RESULT_COMPONENTS: Record<string, Component> = {
  'tool-search_applicants': ResultApplicants,
  'tool-search_cases': ResultCases,
  'tool-get_fund_ledger_snapshot': ResultFundSnapshot,
  'tool-list_funding_sources': ResultFundingSources,
  'tool-list_funds': ResultFunds,
  'tool-list_oversight_funds': ResultFunds,
  'tool-list_staff_users': ResultStaff,
  'tool-list_service_area_staff': ResultStaff,
  'tool-list_fund_ledger_transactions': ResultLedger,
  'tool-list_funding_source_ledger': ResultLedger,
  'tool-list_appointments': ResultAppointments,
  'tool-get_funding_source_ledger_snapshot': ResultFundingSourceSnapshot,
};

const richComponent = (part: any): Component | null => {
  const component = RESULT_COMPONENTS[part?.type];
  if (!component) return null;
  if (part.state !== 'output-available') return null;
  const output = part.output;
  if (!output || typeof output !== 'object' || 'error' in output || output.found === false) return null;
  return component;
};

const containsTable = (text: string) => /\n\|.+\|\s*\n\|[\s:|-]+\|/.test(text ?? '');

// Links in SAMMI's prose (e.g. [AR-10-001-026-224](/applications/{id}/show?arId=…)):
// internal paths navigate in-SPA so the chat stays open; anything else opens a
// new tab. v-html anchors would otherwise trigger a full page load.
const onMarkdownClick = (event: MouseEvent) => {
  const anchor = (event.target as HTMLElement)?.closest?.('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href') ?? '';
  event.preventDefault();
  if (href.startsWith('/')) {
    navigateTo(href);
  } else if (/^https?:\/\//i.test(href)) {
    window.open(href, '_blank', 'noopener');
  }
};

const renderMarkdown = (text: string) => {
  try {
    return DOMPurify.sanitize(marked.parse(text, { async: false, gfm: true, breaks: true }) as string);
  } catch {
    return DOMPurify.sanitize(text);
  }
};

const status = computed(() => agentStore.status);
// Depend on status too: the messages ShallowRef's closing in-place mutation
// doesn't always propagate, but status transitions (submitted/streaming/ready)
// reliably do — so a settling turn repaints its final text even if the array
// reference didn't change. Belt-and-suspenders with AgentStore's flush-on-settle.
const messages = computed<any[]>(() => {
  void status.value;
  return agentStore.messages ?? [];
});
const error = computed(() => agentStore.error);

// Typing dots: waiting for the model (submitted) or streaming but no visible
// text has arrived for this turn yet (e.g. tool-call planning). Hidden once
// text is visibly flowing into the last assistant bubble.
const showTyping = computed(() => {
  if (status.value === 'submitted') return true;
  if (status.value !== 'streaming') return false;
  const last = messages.value[messages.value.length - 1];
  if (!last || last.role !== 'assistant') return true;
  return !last.parts?.some((p: any) => p.type === 'text' && p.text && p.text.length > 0);
});

const isToolPart = (part: any) => {
  try {
    return isToolUIPart(part) || part?.type === 'dynamic-tool';
  } catch {
    return typeof part?.type === 'string' && part.type.startsWith('tool-');
  }
};

const scrollEl = ref<HTMLElement | null>(null);
watch(
  [messages, status, () => agentStore.pendingConfirmation],
  async () => {
    await nextTick();
    scrollEl.value?.scrollTo({ top: scrollEl.value.scrollHeight });
  },
  { deep: true }
);
</script>
<style scoped>
.typing-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: rgb(107 114 128);
  animation: typing-bounce 1.2s ease-in-out infinite;
}
@keyframes typing-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-0.3rem);
    opacity: 1;
  }
}

.agent-markdown :deep(p),
.agent-markdown :deep(ul),
.agent-markdown :deep(ol),
.agent-markdown :deep(table),
.agent-markdown :deep(pre) {
  margin-bottom: 0.5rem;
}
.agent-markdown :deep(> *:last-child),
.agent-markdown :deep(p:last-child) {
  margin-bottom: 0;
}
.agent-markdown :deep(ul) {
  list-style: disc;
  padding-left: 1.25rem;
}
.agent-markdown :deep(ol) {
  list-style: decimal;
  padding-left: 1.25rem;
}
.agent-markdown :deep(a) {
  color: rgb(59 130 246); /* text-blue-500 — matches app link convention */
  cursor: pointer;
}
.agent-markdown :deep(a:hover) {
  color: rgb(37 99 235);
  text-decoration: underline;
}
.agent-markdown :deep(code) {
  background-color: rgb(243 244 246);
  border-radius: 0.25rem;
  padding: 0 0.25rem;
  font-size: 0.75rem;
}
.agent-markdown :deep(pre) {
  background-color: rgb(243 244 246);
  border-radius: 0.25rem;
  padding: 0.5rem;
  overflow-x: auto;
}
/* Tables scroll horizontally inside their own bubble instead of blowing out the window */
.agent-markdown :deep(table) {
  border-collapse: collapse;
  display: block;
  max-width: 100%;
  width: max-content;
  overflow-x: auto;
}
.agent-markdown :deep(th),
.agent-markdown :deep(td) {
  border: 1px solid rgb(156 163 175);
  padding: 0.125rem 0.375rem;
  text-align: left;
  white-space: nowrap;
  font-size: 0.75rem;
}
.agent-markdown :deep(h1),
.agent-markdown :deep(h2),
.agent-markdown :deep(h3),
.agent-markdown :deep(h4) {
  font-weight: 600;
  margin-bottom: 0.25rem;
}
</style>
